import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import eventService from '../../services/eventService';
import { PendingReservation, ApproveReservationRequest } from '../../types/calendar';
import { useFeedback } from '../../contexts/FeedbackContext';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import type { Theme } from '@mui/material/styles';
import { rejectionPanelColors } from '../../theme/readableColors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';

interface PendingReservationsProps {
  /** [Web UX 4. csomag (Admin teendők), 2026-10-04] A lista ÚJ hossza minden sikeres betöltés után (lásd UserApproval). */
  onPendingCountChange?: (count: number) => void;
}

const PendingReservations: React.FC<PendingReservationsProps> = ({ onPendingCountChange }) => {
  const { showSuccess } = useFeedback();
  const [reservations, setReservations] = useState<PendingReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Rejection inline form state
  const [rejectingEventId, setRejectingEventId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Lelet: `PendingReservations.tsx:234`
   * (korábbi sor) — a SOROK ALATTI elutasítási űrlap beírt oka a „Mégse”/X-re, egy MÁSIK sor
   * „Elutasítás” gombjára (az új sor kiüríti a mezőt) és a fülváltásra kérdés nélkül elveszett.
   * Piszkos = nyitott űrlap + nem üres ok (trim után).
   */
  const confirm = useConfirm();
  const reasonDirty = rejectingEventId !== null && isFormDirty({ reason: '' }, { reason: rejectionReason });
  useUnsavedChanges(reasonDirty, 'Elutasítás oka');
  const confirmDiscardReason = async () =>
    !reasonDirty || confirm(discardChangesConfirm('elutasítás oka'));

  useEffect(() => {
    loadPendingReservations();
  }, []);

  const loadPendingReservations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await eventService.getPendingReservations();
      setReservations(data);
      onPendingCountChange?.(data.length);
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a foglalások betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId: string) => {
    try {
      setActionLoading(eventId);
      setError('');
      const request: ApproveReservationRequest = {
        isApproved: true
      };
      await eventService.approveReservation(eventId, request);
      showSuccess('A foglalás jóváhagyva.');
      await loadPendingReservations();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a jóváhagyás során'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClick = async (eventId: string) => {
    if (!(await confirmDiscardReason())) return;
    setRejectingEventId(eventId);
    setRejectionReason('');
    setError('');
  };

  const handleCancelReject = async () => {
    if (rejectingEventId !== null && actionLoading === rejectingEventId) return;
    if (!(await confirmDiscardReason())) return;
    setRejectingEventId(null);
    setRejectionReason('');
    setError('');
  };

  const handleRejectConfirm = async (eventId: string) => {
    if (!rejectionReason.trim()) {
      setError('Kérem adja meg az elutasítás okát');
      return;
    }

    try {
      setActionLoading(eventId);
      setError('');
      const request: ApproveReservationRequest = {
        isApproved: false,
        rejectionReason: rejectionReason
      };
      await eventService.approveReservation(eventId, request);
      setRejectingEventId(null);
      setRejectionReason('');
      showSuccess('A foglalás elutasítva.');
      await loadPendingReservations();
    } catch (err: any) {
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      setError(saveErrorMessage(err, 'Hiba történt az elutasítás során'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          Jóváhagyásra váró asztalfoglalások
        </Typography>
        <Button
          variant="outlined"
          onClick={loadPendingReservations}
          disabled={loading}
        >
          Frissítés
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : reservations.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" align="center">
            Nincs jóváhagyásra váró asztalfoglalás
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Foglalás neve</strong></TableCell>
                <TableCell><strong>Létrehozó</strong></TableCell>
                <TableCell><strong>Dátum</strong></TableCell>
                <TableCell><strong>Időpont</strong></TableCell>
                <TableCell><strong>Helyszín</strong></TableCell>
                <TableCell align="center"><strong>Asztalok</strong></TableCell>
                <TableCell><strong>Megjegyzés</strong></TableCell>
                <TableCell align="center"><strong>Műveletek</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.map((reservation) => (
                <React.Fragment key={reservation.eventId}>
                  <TableRow hover>
                    <TableCell>{reservation.eventName}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{reservation.createdByName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reservation.createdByEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(reservation.reservationDate)}</TableCell>
                    <TableCell>
                      {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                    </TableCell>
                    <TableCell>{reservation.venueName}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${reservation.requiredTables} db`}
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      {reservation.notes || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => handleApprove(reservation.eventId)}
                          disabled={actionLoading === reservation.eventId || rejectingEventId === reservation.eventId}
                        >
                          Jóváhagyás
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => handleRejectClick(reservation.eventId)}
                          disabled={actionLoading === reservation.eventId || rejectingEventId === reservation.eventId}
                        >
                          Elutasítás
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {/* Inline rejection form */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: rejectingEventId === reservation.eventId ? 1 : 0 }}>
                      <Collapse in={rejectingEventId === reservation.eventId} timeout="auto" unmountOnExit>
                        {/* [Web 7 / T2, 2026-10-06] WEB_UX_2KOR (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 4.1: az `error.lighter` palettakulcs a MUI 7.3.2-ben NEM létezik →
                            a panel háttere sosem jelent meg; az `error.dark` cím sötét témában 1,97:1, a `bgcolor: 'white'` szövegmező
                            sötét témában világos betűvel fehéren 1,12:1 volt. Közös, kontraszt-őrrel mért recept: `rejectionPanelColors`.
                            [Web 7 / T2 javító kör 2, 2026-10-06 — M-E] Pontosítás: az 1,12:1 a szövegmező GYÖKERÉNEK (fehér `bgcolor`) felszínére igaz;
                            sötétben a beírt szöveg valójában a `.MuiOutlinedInput-root` saját `#242A30` hátterén jelenik meg (`darkTheme.ts` MuiTextField felülírás). */}
                        <Box data-testid="rejection-panel" sx={{ py: 2, px: 2, bgcolor: (t: Theme) => rejectionPanelColors(t).bg, borderRadius: 1, my: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: (t: Theme) => rejectionPanelColors(t).title }}>
                              Elutasítás oka
                            </Typography>
                            {/* [Web UX 9 / T5, 2026-10-13] a bezáró X címke nélküli volt (UX-lista 6.1, `PendingReservations.tsx:249`). A név szándékosan
                                NEM tartalmazza az „Elutasítás"/„Mégse" szót (részszöveg-illesztésnél ütközne a panel gombjaival). */}
                            <IconButton size="small" onClick={handleCancelReject} aria-label="Panel bezárása">
                              <CloseIcon />
                            </IconButton>
                          </Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Kérem adja meg az elutasítás okát, amit a felhasználó látni fog..."
                            variant="outlined"
                            data-testid="rejection-reason-field"
                            sx={{ mb: 2, bgcolor: (t: Theme) => rejectionPanelColors(t).field }} /* [Web 7 / T2 javító kör 1, 2026-10-06 — F3] a szövegmező háttere is a közös receptből */
                          />
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              onClick={handleCancelReject}
                            >
                              Mégse
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={<RejectIcon />}
                              onClick={() => handleRejectConfirm(reservation.eventId)}
                              disabled={!rejectionReason.trim() || actionLoading === reservation.eventId}
                            >
                              Elutasítás
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PendingReservations;
